import React, { useState } from 'react';
import PropTypes from 'prop-types';
import styledComponent from '../../utils/styledComponent';
import theme from '../../theme';

const StyledPanel = styledComponent('div', {
  border: `1px solid ${theme.colors.borderLight}`,
  borderRadius: theme.radii.inner,
  background: theme.colors.bgMuted,
  overflow: 'hidden',
  marginBottom: theme.spacing.gapXl,
  maxWidth: '720px',
  transition:
    'box-shadow 0.15s ease, background 0.15s ease, border-color 0.15s ease',

  '&[data-open="true"]': {
    background: theme.colors.bgCard,
    borderColor: theme.colors.border,
    boxShadow: theme.shadows.card,
  },

  '& .tp-toggle': {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing.gapLg,
    width: '100%',
    padding: theme.spacing.cellPadding,
    background: 'transparent',
    border: 'none',
    font: 'inherit',
    fontSize: theme.typography.body.fontSize,
    fontWeight: theme.fontWeights.semibold,
    color: theme.colors.textMuted,
    cursor: 'pointer',
    textAlign: 'left',
  },

  '& .tp-toggle:hover': {
    color: theme.colors.primary,
  },

  '&[data-open="true"] .tp-toggle': {
    color: theme.colors.textBody,
  },

  '& .tp-chevron': {
    display: 'inline-flex',
    width: theme.sizes.iconSm,
    height: theme.sizes.iconSm,
    alignItems: 'center',
    justifyContent: 'center',
    color: theme.colors.textHint,
    transition: 'transform 0.15s ease, color 0.15s ease',
    fontSize: theme.typography.badge.fontSize,
    fontWeight: theme.fontWeights.semibold,
  },

  '&[data-open="true"] .tp-chevron': {
    transform: 'rotate(90deg)',
    color: theme.colors.primary,
  },

  '& .tp-body': {
    padding: theme.spacing.tipsBodyPadding,
    display: 'grid',
    gap: theme.spacing.fieldGap,
  },

  '& .tp-tip-title': {
    fontSize: theme.typography.tips.fontSize,
    fontWeight: theme.fontWeights.semibold,
    color: theme.colors.textBody,
    margin: `0 0 ${theme.spacing.hintGap}`,
  },

  '& .tp-tip-body': {
    fontSize: theme.typography.tips.fontSize,
    fontWeight: theme.typography.tips.fontWeight,
    color: theme.colors.textMuted,
    lineHeight: theme.lineHeights.body,
    margin: 0,
  },

  '& .tp-tip-body strong': {
    color: theme.colors.textBody,
    fontWeight: theme.fontWeights.semibold,
  },

  '& .tp-tip-body code': {
    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
    fontSize: theme.typography.inlineCode.fontSize,
    fontWeight: theme.typography.inlineCode.fontWeight,
    background: theme.colors.bgMuted,
    color: theme.colors.textBody,
    padding: theme.spacing.inlineCodePadding,
    borderRadius: theme.radii.small,
    border: `1px solid ${theme.colors.borderLight}`,
  },

  '&[data-open="true"] .tp-tip-body code': {
    background: theme.colors.bgPage,
  },
});

function TipsPanel({ label, tips }) {
  const [open, setOpen] = useState(false);
  return (
    <StyledPanel data-open={open}>
      <button
        type="button"
        className="tp-toggle"
        onClick={() => setOpen((prev) => !prev)}
        aria-expanded={open}
      >
        <span className="tp-chevron" aria-hidden="true">
          ▸
        </span>
        {label}
      </button>
      {open && (
        <div className="tp-body">
          {tips.map((tip, idx) => (
            <div key={`${idx}-${tip.title}`}>
              <p className="tp-tip-title">{tip.title}</p>
              <p className="tp-tip-body">{tip.body}</p>
            </div>
          ))}
        </div>
      )}
    </StyledPanel>
  );
}

TipsPanel.propTypes = {
  label: PropTypes.string,
  tips: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string.isRequired,
      body: PropTypes.node.isRequired,
    }),
  ).isRequired,
};

TipsPanel.defaultProps = {
  label: 'How to use this',
};

export default TipsPanel;
